import { Injectable } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

import { Account, } from '@mitel/cloudlink-sdk';
import { AppInjector } from './../../app-injector';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { IntegUnifyPhoneSharedService } from './integ-unify-phone-shared.service';
import { TenantsService } from './tenants.service';
import { SpinnerService } from './spinner.service';
import { SipTrunksConfigComponent } from './../../modules/integrations-applications/components/integ-config-dialogs/sip-trunks-config/sip-trunks-config.component';
import { ProgressIndicatorPopupComponent } from './../../shared/components/progress-indicator-popup/progress-indicator-popup.component';
import { IntegrationItemServiceInf, StatusString } from '../interfaces/integrations';
import { Application } from '../interfaces/system-manager';
import { TenantSipTrunk } from '../interfaces/tenants';
import { Util } from '../utils/Util';

export interface DeleteSIPTrunkFail {
  trunkId: string;
  error: { body: any, statusCode: number };
}

const logContext = 'IntegSipTrunksService';
@Injectable({
  providedIn: 'root'
})
export class IntegSipTrunksService implements IntegrationItemServiceInf {
  private adminService: AdminService;
  private accountsService: AccountsService;
  private tenantsService: TenantsService;
  private integUnifyPhoneSharedService: IntegUnifyPhoneSharedService;
  private modalService: NgbModal;
  private translateService: TranslateService;
  private spinnerService: SpinnerService;
  
  constructor() { }

  initServices() {

    if (!this.adminService)
      this.adminService = AppInjector.injector.get(AdminService);

    if (!this.accountsService)
      this.accountsService = AppInjector.injector.get(AccountsService);

    if (!this.integUnifyPhoneSharedService)
      this.integUnifyPhoneSharedService = AppInjector.injector.get(IntegUnifyPhoneSharedService);

    if (!this.tenantsService)
      this.tenantsService = AppInjector.injector.get(TenantsService);

    if (!this.translateService)
      this.translateService = AppInjector.injector.get(TranslateService);

    if (!this.modalService)
      this.modalService = AppInjector.injector.get(NgbModal);

    if (!this.spinnerService)
      this.spinnerService = AppInjector.injector.get(SpinnerService);
  }

  canBeSelected(account: Account): Promise<boolean> {
    return Promise.resolve(false);
  }

  getConfigurationDialog(): any {
    return SipTrunksConfigComponent;
  }
  
  async isSetupComplete(account: Account): Promise<boolean> {
    this.initServices();

    try {
      let canConfigure = await this.canConfigure(account);
      if (!canConfigure) {
        return true;
      }
      const tags = await this.adminService.getAccountTagsNoFrills(account.accountId);
      return tags?.SIPTrunks?.setupComplete == true;
    } catch (error) {
      console.error(`${logContext}.isSetupComplete - Error determining the account tags: `, error);
      return false;
    }
  }

  isIntegrationStateChangeable(account: Account): Promise<boolean> {
    return Promise.resolve(false);
  }
  
  handleIntegrationStateChange(account: Account, state: boolean): Promise<any> {
    return Promise.resolve();
  }

  async removeAsASubintegration(account: Account): Promise<any> {
    this.initServices();

    try {
      this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
      this.spinnerService.showSpinner();

      let tags = await this.adminService.getAccountTagsNoFrills(account.accountId);

      if (tags?.SIPTrunks) {
        try {
          await this.adminService.deleteAccountTag(account.accountId, 'SIPTrunks');
        }
        catch (error) {
          console.error(`${logContext}.removeAsASubintegration - Failed to delete the SIP Trunks tag: `, error);
          throw error;
        }
      }

      this.spinnerService.hideSpinner();
      return true;
    } catch (error) {
      console.error(`${logContext}.removeAsASubintegration - Error determining the account tags: `, error);
      this.spinnerService.hideSpinner();
      throw error;
    };
  }
  
  async isIntegrationStateOn(account: Account): Promise<boolean> {
    this.initServices();

    try {
      const tags = await this.adminService.getAccountTagsNoFrills(account.accountId);
      return tags?.SIPTrunks?.setupComplete == true;
    } catch (error) {
      console.error(`${logContext}.isIntegrationStateOn - Error determining the account tags: `, error);
      return false;
    }
  }	
  
  async canConfigure(account: Account): Promise<boolean> {
    this.initServices();
    return this.integUnifyPhoneSharedService.canConfigureSIPTrunks(account);
  }

  getStatusString(account: Account): Promise<StatusString> {
    return undefined;
  }

  getConfigButtonLabel(account: Account): Promise<string> {
    return undefined;
  }

  isButtonDisabled(account: Account): Promise<boolean> {
    return Promise.resolve(false);
  }

  async saveConfigurationData(account: Account, sipTrunkConfig: any): Promise<boolean> {
    this.initServices();

    try {
      this.spinnerService.setSpinnerMessageByKey('spinnerMessages.updatingAccount');
      this.spinnerService.showSpinner();
      let dataChanges: any = {
        tags: {
          SIPTrunks: {
            ...sipTrunkConfig,
            setupComplete: true
          }
        }
      };

      const updatedAccount = await this.adminService.updateAccount(account.accountId, dataChanges);
      this.accountsService.mergeAccountWithModel(updatedAccount);
      this.spinnerService.hideSpinner();
      return true;
    } catch (error) {
      console.error(`${logContext}.saveConfigurationData - Failed to update account tags: `, error);
      this.spinnerService.hideSpinner();
      throw error;
    }
  }

  canDeleteIntegration(account: Account): Promise<boolean> {
    return Promise.resolve(false);
  }

  async getIntegrationTags(account: Account): Promise<any> {
    this.initServices();

    try {
      const tags = await this.adminService.getAccountTagsNoFrills(account.accountId);
      return tags?.SIPTrunks;
    } catch (error) {
      console.error(`${logContext}.getIntegrationTags - Error determining the account tags`, error);
      return undefined;
    }
  }

  async getSIPApplications(account: Account): Promise<Application[]> {
    this.initServices();
    return this.integUnifyPhoneSharedService.getSIPApplications(account);
  }
  
  async getSIPTrunks(account: Account): Promise<TenantSipTrunk[]> {
    this.initServices();
    return this.integUnifyPhoneSharedService.getSIPTrunks(account);
  }

  async addSIPTrunk(account: Account, sipTrunk: TenantSipTrunk): Promise<TenantSipTrunk> {
    this.initServices();

    try {
      const tenant = await this.integUnifyPhoneSharedService.getTenant(account);
      if(tenant?.tenantId) {
        return this.tenantsService.createTenantSipTrunk(tenant.tenantId, sipTrunk);
      } else {
        console.error(`${logContext}.AddSIPTrunk - Error adding a new SIP Trunk ( workflow ): Missing tenant for account`);
        throw 'Missing tenant for account';
      }
    } catch (error) {
      console.error(`${logContext}.AddSIPTrunk - Error adding a new SIP Trunk ( workflow ) `, sipTrunk, error);
      throw error;
    }
  }
  
  async updateSIPTrunk(account: Account, sipTrunk: TenantSipTrunk): Promise<TenantSipTrunk> {
    this.initServices();

    try {
      const tenant = await this.integUnifyPhoneSharedService.getTenant(account);
      if(tenant?.tenantId) {
        return this.tenantsService.updateTenantSipTrunk(tenant.tenantId, sipTrunk);
      } else {
        console.error(`${logContext}.updateSIPTrunk - Error updating a SIP Trunk ( workflow ): Missing tenant for account`);
        throw 'Missing tenant for account';
      }
    } catch (error) {
      console.error(`${logContext}.updateSIPTrunk - Error updating a SIP Trunk ( workflow ) `, sipTrunk, error);
      throw error;
    }
  }

  async deleteSIPTrunks(account: Account, sipTrunks: TenantSipTrunk[]): Promise<string[]> {
    this.initServices();

    try {
      if (sipTrunks.length > 0) {
        const tenant = await this.integUnifyPhoneSharedService.getTenant(account);
        if (tenant?.tenantId) {
          const failedSIPTrunks = await this.deleteTenantSIPTrunks(tenant.tenantId, sipTrunks);
          const sipTrunkIds = sipTrunks.filter(trunk => !failedSIPTrunks.some(failedAccount => failedAccount.trunkId === trunk.trunkId)).map(trunk => trunk.trunkId);
          return sipTrunkIds;
        } else {
          const message = 'Missing Unify Phone tenant for account'
          console.error(`${logContext}.deleteSIPTrunks - Error deleting SIP Trunks ( workflow ): ${message}`);
          throw message;
        }
      }
      return [];
    } catch (error) {
      console.error(`${logContext}.deleteSIPTrunks - Error deleting SIP Trunks ( workflow ) `, error);
      throw error;
    }
  }

  async deleteTenantSIPTrunks(tenantId: string, sipTrunks: TenantSipTrunk[]): Promise<DeleteSIPTrunkFail[]> {
    this.initServices();
    
    const space = '<br/>&nbsp;&nbsp;';
    const plural = sipTrunks?.length > 1 ? 's' : '';
    let progressUpdate = new Subject();
    let progressData = {
      total: sipTrunks.length,
      done: 0,
      errorMessage: undefined,
      title: `integrations.sip-trunks.progress-indicator.delete-sip-trunk${plural}-title`,
      actionString: `integrations.sip-trunks.progress-indicator.delete-sip-trunk${plural}-action`,
      successMessage: undefined,
      updateSubject: progressUpdate
    }
    let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modal.componentInstance.progressData = progressData;

    let failedSIPTrunkIds: DeleteSIPTrunkFail[] = [];

    for (var i = 0; i < sipTrunks.length; i++) {
      const sipTrunk = sipTrunks[i];
      // to prevent the client from hammering the system
      await Util.timeout(10);

      try {
        await this.tenantsService.deleteTenantSipTrunk(tenantId, sipTrunk.trunkId);
      } catch (error) {
        console.error(`Error has occurred while trying to delete SIP trunk: ${sipTrunk.trunkId}`, error);
        let errorInfo: DeleteSIPTrunkFail = {
          trunkId: sipTrunk.trunkId,
          error: { body: error.body, statusCode: error.statusCode }
        }
        failedSIPTrunkIds.push(errorInfo);

        if (error.statusCode && error.statusCode == 404) {
          continue;
        }

        if (!progressData.errorMessage) {
          let message = `integrations.sip-trunks.progress-indicator.delete-sip-trunk${plural}-failed`;
          progressData.errorMessage = this.translateService.instant(message) + ':' + space + sipTrunk.trunkName;
        }
        else { // just append the name after a newline
          progressData.errorMessage = progressData.errorMessage + space + sipTrunk.trunkName;
        }
      }

      progressData.done++;
      progressUpdate.next(progressData);
    };

    progressData.successMessage = this.getFinalMessage(progressData.done - failedSIPTrunkIds.length);
    progressUpdate.next(progressData);

    return modal.result.then(result => {
      modal = null;
      progressUpdate = null;
      console.log('Here is the failed list', failedSIPTrunkIds);
      return failedSIPTrunkIds;
    });
  }

  getFinalMessage(numSIPTrunks: number): string {
    if (numSIPTrunks > 0){
      const plural = numSIPTrunks > 1 ? 's' : '';
      return this.translateService.instant(`integrations.sip-trunks.progress-indicator.delete-sip-trunk${plural}-done`, { numSIPTrunks: numSIPTrunks });
    }
    else
      return this.translateService.instant('integrations.sip-trunks.progress-indicator.delete-sip-trunks-none-done');
  }
}
